/**
 * The write hooks the Elevate capture card uses — each a GOVERNED verb, never a
 * raw insert (#998, skill rule 7). They are the SAME verbs the record cascade's
 * quick-create reaches through `core.mutate_relationship` and the engagements module
 * triggers back with owner/authority/event/notify — so capturing from the rail
 * and capturing on the record are one governed path, not two.
 *
 *   record_decision  — the decisions log, emits engagements.decision.recorded
 *   assign_action    — owner + due; notifies the assignee
 *   add_open_point   — the running list
 *
 * Every mutation invalidates the interaction's own read AND the generic related
 * caches, so a capture made from the rail lights up the meeting record's cascade
 * without a reload.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'
import { reconcileRelationships } from '../data/queries'

/** The record these captures hang off. See `onSuccess` below. */
const ENGAGEMENT_ENTITY = 'engagements_engagement'

function useCaptureMutation<V>(fn: (raw: ReturnType<ReturnType<typeof useSolum>['client']['raw']>, v: V) => Promise<unknown>, meetingId: string | null) {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (v: V) => fn(client.raw(), v),
    /* The capture card writes a decision, an action or an open point, and each
       of those is the far end of a registered fk relationship on the meeting.
       So a capture moves the meeting's relationship rows AND its counts, and
       must reconcile them the same way a link does (#2000) — scoped to this
       meeting rather than to every related list on screen, and AWAITED so the
       toast cannot claim a capture the cascade is not showing yet.

       The entity key is named here because this card is the ENGAGEMENT's, by
       construction: `meetingId` is an `engagements.engagement` id and nothing
       else can be passed to it. `elevateTimeline.ts` names the same key for
       the same reason. */
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['interaction', meetingId] })
      void qc.invalidateQueries({ queryKey: ['interactions'] })
      return meetingId ? reconcileRelationships(qc, ENGAGEMENT_ENTITY, meetingId) : undefined
    },
  })
}

export function useRecordDecision(meetingId: string | null) {
  return useCaptureMutation<{ statement: string }>(
    (raw, { statement }) => raw.rpcScalar<string>('record_decision',
      { p_engagement_id: meetingId, p_statement: statement }, 'engagements'), meetingId)
}

export function useAssignAction(meetingId: string | null) {
  return useCaptureMutation<{ title: string; assignee: string | null; due: string | null }>(
    (raw, { title, assignee, due }) => raw.rpcScalar<string>('assign_action',
      { p_engagement_id: meetingId, p_title: title, p_assignee: assignee, p_due_at: due || null }, 'engagements'), meetingId)
}

export function useRaiseOpenPoint(meetingId: string | null) {
  return useCaptureMutation<{ title: string; due: string | null }>(
    (raw, { title, due }) => raw.rpcScalar<string>('add_open_point',
      { p_engagement_id: meetingId, p_title: title, p_body: null, p_due_at: due || null }, 'engagements'), meetingId)
}
